import { useEffect } from "react";
import { Navigate, useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { get, imgUrl } from "@/lib/api";
import { useSeo } from "@/lib/layout";
import { fmtDate } from "@/lib/format";
import { Seo, orgLd, breadcrumbLd } from "@/components/site/Seo";
import { PageBody } from "@/components/site/PageBody";
import NotFound from "@/pages/NotFound";

const Skeleton = () => (
  <div className="wrap pt-10 md:pt-14 max-w-6xl animate-pulse space-y-5" data-testid="page-loading">
    <div className="h-4 w-32 bg-muted" /><div className="h-16 bg-muted w-4/5" /><div className="h-6 bg-muted w-3/5" />
  </div>
);

export default function ContentPage() {
  const { slug } = useParams();
  const { hash } = useLocation();
  const { seo, site } = useSeo();
  const { data: p, isLoading, isError } = useQuery({ queryKey: ["page", slug], queryFn: () => get(`/public/pages/${slug}`), retry: false });
  useEffect(() => {
    if (p?.body && hash) setTimeout(() => document.getElementById(decodeURIComponent(hash.slice(1)))?.scrollIntoView({ behavior: "smooth" }), 50);
  }, [p, hash]);

  if (isLoading) return <Skeleton />;
  if (p?.redirect) return <Navigate to={`/${p.redirect}${hash}`} replace />;
  if (isError || !p) return <NotFound />;
  const ld = p.key === "about"
    ? [{ ...orgLd(seo, site), "@type": ["NewsMediaOrganization", "AboutPage"] }]
    : [{ "@context": "https://schema.org", "@type": "WebPage", name: p.title, description: p.seo_description || p.lead, inLanguage: "es-ES", dateModified: p.updated_at },
       breadcrumbLd([{ name: "Portada", path: "/" }, { name: p.nav_label || p.title, path: `/${p.slug}` }])];

  return (
    <div className="wrap pt-10 md:pt-14 max-w-6xl" data-testid={`content-page-${p.slug}`}>
      <Seo title={p.seo_title || p.title} description={p.seo_description || p.lead} image={imgUrl(p.share_image) || undefined} noindex={p.noindex} jsonLd={ld} />
      {p.kicker && <p className="kicker text-primary" data-testid="page-kicker">{p.kicker}</p>}
      <h1 className="font-display font-black tracking-[-0.03em] text-4xl sm:text-5xl lg:text-6xl mt-2 max-w-4xl leading-[1.02] break-words" data-testid="page-title">{p.title}</h1>
      {p.lead && <p className="mt-8 font-serif text-xl sm:text-2xl text-muted-foreground max-w-3xl leading-relaxed" data-testid="page-lead">{p.lead}</p>}
      {p.show_updated && <p className="mt-6 font-mono text-[11px] uppercase tracking-[0.1em] text-muted-foreground" data-testid="page-updated">Última actualización: {fmtDate(p.updated_at)}</p>}
      <div className="mt-14"><PageBody html={p.body} /></div>
    </div>
  );
}
